import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { actions } from '../../SuperUser/Report/redux/actions';
import { graphContainer } from './graphContainers';
import { ColorPalette } from '../DashboardColorPalettes';
import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { genIntervalName } from './MetricTrendAllIntervals';
import Strings from '../../../Strings';
import { getProgramDisplayName } from '../../../utils/cmsPrograms';

export const MetricsForTrendPlotStacked = {
  cpt_combinations: {
    id: 'cpt_combinations',
    label: 'Patients with CPT Codes',
    series: ["0E0M", "0E1M", "0E2M", "0E3M", "1E0M", "1E1M", "1E2M", "1E3M"],
    columnTitle: 'Codes combination',
  },
  days_with_vitals: {
    id: 'days_with_vitals',
    label: 'Patients with readings for X days',
    series: Array.from(Array(32).keys()).map(x => x.toString()),
    columnTitle: 'Number of days',
  },
  cpt_codes: {
    id: 'cpt_codes',
    label: 'Number of CPT Codes',
    series: ["CPT 99453", "CPT 99454", "CPT 99457", "CPT 99458 #1", "CPT 99458 #2", "CPT 98975", "CPT 98976", "CPT 98977", "CPT 98978", "CPT 98980", "CPT 98981 #1", "CPT 98981 #2"],
    seriesForSelect: [
      { label: 'RTM', options: ["CPT 99453", "CPT 99454", "CPT 99457", "CPT 99458 #1", "CPT 99458 #2"].map(c => ({ label: c, value: c, group: "RTM" }))},
      { label: 'RPM', options: ["CPT 98975", "CPT 98976", "CPT 98977", "CPT 98978", "CPT 98980", "CPT 98981 #1", "CPT 98981 #2"].map(c => ({ label: c, value: c, group: "RPM" }))},
    ],
    columnTitle: 'Code',
  },
  patientsPerProgram: {
    id: 'patientsPerProgram',
    label: 'Patients per Program',
    series: ['pcm', 'ccmBasic', 'ccmComplex', 'rtmResp', 'rtmMs', 'rtmBeh', 'rpm'],
    columnTitle: 'Program',
  },
  adherence: {
    id: 'adherence',
    label: 'Adherence Distribution',
    series: Array.from({ length: 11 }, (_, i) => (i * 10).toString()),
    columnTitle: 'Adherence',
  }
};

export const translateSeriesNames = (metric, name) => {
  if (metric === 'days_with_vitals' && !isNaN(name)) return `Patients with ${name} ${name == 1 ? 'day' : 'days'} of readings`;
  if (metric === 'cpt_combinations') {
    const codePattern = /(\d+)E(\d+)M/;
    const match = codePattern.exec(name);
    if (match) {
      const deviceCodes = match[1];
      const monitoringCodes = match[2];
      return `${deviceCodes} device code${deviceCodes === '1' ? '' : 's'}, ${monitoringCodes} monitoring code${monitoringCodes === '1' ? '' : 's'}`;
    }
  }
  if (metric === 'patientsPerProgram') {
    switch (name) {
      case 'ccmBasic':
        return getProgramDisplayName('CCM', 'CCMBasic');
      case 'ccmComplex':
        return getProgramDisplayName('CCM', 'CCMComplex');
      case 'rtmResp':
        return getProgramDisplayName('RTM', 'RTMRespiratory');
      case 'rtmMs':
        return getProgramDisplayName('RTM', 'RTMMusculoskeletal');
      case 'rtmBeh':
        return getProgramDisplayName('RTM', 'RTMBehavioralHealth');
      case 'rpm':
        return getProgramDisplayName('RPM');
      case 'pcm':
        return getProgramDisplayName('PCM');
      default:
        return name;
    }
  }

  // if (metric === 'cpt_codes_statistics') {
  //   switch (name) {
  //     case 'CPT 99453':
  //       return 'RPM Setup';
  //     case 'CPT 99454':
  //       return 'RPM Biometric Monitoring';
  //     case 'CPT 99457':
  //       return 'RPM Working on Patient Data (First)';
  //     case 'CPT 99458 #1':
  //       return 'RPM Working on Patient Data (Next)';
  //     case 'CPT 99458 #2':
  //       return 'RPM Working on Patient Data (Next) (2)';
  //     case 'CPT 98975':
  //       return 'RTM Setup';
  //     case 'CPT 98976':
  //       return 'RTM Respiratory Monitoring';
  //     case 'CPT 98977':
  //       return 'RTM Musculoskeletal Monitoring';
  //     case 'CPT 98978':
  //       return 'RTM Behavioral Health Monitoring';
  //     case 'CPT 98980':
  //       return 'RTM Consulting (First)';
  //     case 'CPT 98981 #1':
  //       return 'RTM Consulting (Next)';
  //     case 'CPT 98981 #2':
  //       return 'RTM Consulting (Next) (2)';
  //     default:
  //       return name;
  //   }
  // }
  return name;
};

export const getStackedMetricTrendMetadata = metric => {
  const series = MetricsForTrendPlotStacked[metric]?.series || [];
  return {
    id: `StackedMetricTrend_${metric}`,
    name: MetricsForTrendPlotStacked[metric]?.label,
    permission: '',
    defProps: { i: `StackedMetricTrend_${metric}`, w: 4, h: 4, minW: 2, minH: 4 },
    configuration: {
      chartType: {
        type: 'select',
        name: 'chartType',
        label: 'Chart Type',
        defaultValue: chartTypeEnum.horizontalStackedBar,
        options: [
          { label: 'Stacked Bar chart', value: chartTypeEnum.stackedBar },
          { label: 'Stacked Horizontal Bar', value: chartTypeEnum.horizontalStackedBar },
          { label: 'Table', value: 'table' }
        ],
        canBeSetByDashboard: false,
      },
      plotInterval: {
        type: 'select',
        name: 'plotInterval',
        label: 'Period Type',
        defaultValue: 'months',
        options: [
          { label: 'Days', value: 'days' },
          { label: 'Weeks', value: 'weeks' },
          { label: 'Months', value: 'months' },
        ],
        canBeSetByDashboard: false,
        hidden: metric === 'days_with_vitals' || metric === 'cpt_combinations' || metric === 'cpt_codes',
      },
      rollingPeriod: {
        type: 'select',
        name: 'rollingPeriod',
        label: 'Rolling Period (days / weeks / months)',
        defaultValue: 4,
        options: [
          { label: '1', value: 1 },
          { label: '2', value: 2 },
          { label: '3', value: 3 },
          { label: '4', value: 4 },
          { label: '5', value: 5 },
          { label: '6', value: 6 },
        ],
        canBeSetByDashboard: false,
      },
      metrics: {
        type: 'multiselect',
        name: 'metrics',
        label: MetricsForTrendPlotStacked[metric]?.columnTitle || "Metrics",
        defaultValue: series,
        options: MetricsForTrendPlotStacked[metric]?.seriesForSelect || [ { label: Strings.all, options: series.map(s => ({ label: translateSeriesNames(metric, s), value: s })) }],
        canBeSetByDashboard: false,
      },
    },
  };
};

export const getDataSeries = (metric, metrics, series, data, colorPalette) => {
  if (!data?.length || !series?.length) return [];
  const ret = [];

  series.filter(x => metrics.includes(x)).forEach(serie => {
    const name = translateSeriesNames(metric, serie);
    data.forEach(entry => {
      if (entry[name] && !ret.some(item => item.name === name)) {
        ret.push({
          name,
          selector: item => item[name],
        });
      }

    })
  })

  ret.forEach((item, i) => {
    item.color = colorPalette?.interpolate((1 / (ret.length - 1 || 1)) * i);
  });

  return ret;
};

function StackedMetricTrend(props) {
  const { colorPalette, graphType, plotInterval, rollingPeriod, metric, metrics, data } = props;

  const graphData = Array.from({ length: rollingPeriod }, (_, i) => {
    if (!data?.[plotInterval]?.[-i]?.[metric])
      return { period: genIntervalName(plotInterval, i) };
    return {
      period: genIntervalName(plotInterval, i),
      ...Object.entries(data?.[plotInterval]?.[-i]?.[metric])
        .filter(x => x[1])
        .filter(x => metrics.includes(x[0]))
        .reduce((acc, curr) => ({ ...acc, [translateSeriesNames(metric, curr[0])]: curr[1] }), {}),
    };
  });

  const series = MetricsForTrendPlotStacked[metric]?.series || [];
  const dataSeries = getDataSeries(metric, metrics, series, graphData, colorPalette);

  return graphContainer(
    <GenericCategoryGraph
      graphData={graphData}
      hasBorder
      forceCondensed
      xAxisTopPosition={false}
      yAxisWidth={graphType === chartTypeEnum.horizontalStackedBar ? 100 : 40}
      yAxisUnit=""
      dataSeries={dataSeries}
      categorySelector={item => item.period}
      hasReferenceAreas
      graphType={graphType}
      colorPalette={colorPalette}
    />,
    MetricsForTrendPlotStacked[metric]?.label,
  );
}

StackedMetricTrend.propTypes = {
  getPatientsStatistics: PropTypes.func,
  onClick: PropTypes.func,
  colorPalette: PropTypes.any,
  refreshTimestamp: PropTypes.any,
  graphType: PropTypes.string,
  dragging: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    colorPalette:
      ColorPalette[state.dashboardState.configuration?.allDashboards?.colorPalette] || Object.values(ColorPalette)[0],
  };
};


const mapDispatchToProps = dispatch => ({
  getEnrollmentsMetrics: pageRequest => dispatch(actions.getEnrollmentsMetrics(pageRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StackedMetricTrend);
